import type { RawImageRef } from "@kraahl/contract"
import type { Iso4217 } from "@kraahl/contract"

export type OfferStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "counter_offered"
  | "expired"
  | "withdrawn"

export type OfferDeclineReason =
  | "below_minimum"
  | "seller_declined"
  | "buyer_declined"
  | "listing_sold_via_other_offer"
  | "listing_deleted"
  | string

// Offers are single-currency (offers.currency = listings.currency = profiles.currency).
// Align with the canonical marketplace currency union — a narrow EUR/USD/GBP type
// silently mis-typed JPY/TWD/CAD offers.
export type OfferCurrency = Iso4217

export interface OfferRow {
  id: string
  listing_id: string
  buyer_id: string
  seller_id: string
  amount: number
  status: OfferStatus
  message: string | null
  shipping_method_id: string | null
  shipping_amount: number
  currency: OfferCurrency
  expires_at: string
  parent_offer_id: string | null
  client_offer_id: string | null
  decline_reason: OfferDeclineReason | null
  event_version: number
  accepted_at: string | null
  declined_at: string | null
  countered_at: string | null
  withdrawn_at: string | null
  expired_at: string | null
  payment_deadline: string | null
  payment_timeout_notified_at: string | null
  settlement_order_id: string | null
  settled_at: string | null
  reminder_sent_at: string | null
  created_at: string
  updated_at: string
}

export interface OfferProfile {
  id: string
  username: string
  avatar_url: string | null
}

export interface OfferListing {
  id: string
  title: string
  price: number | null
  currency: OfferCurrency | null
  /**
   * `listings.image_urls` AS STORED. Raw on purpose: `OfferCheckoutSheet`
   * feeds `image_urls[0]` into the checkout snapshot, which `createOrder`
   * persists verbatim into `order_items.image_url`. Producing RESOLVED urls
   * here put absolute Supabase URLs back into the DB on every offer settle
   * (found 2026-07-27 by the `RawImageRef` brand). Display consumers
   * (`OfferCard`, offer emails) resolve at their own sinks.
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §9
   */
  image_urls: RawImageRef[]
  user_id: string
  listing_format: string
  deleted_at: string | null
}

export interface OfferWithRelations extends OfferRow {
  buyer: OfferProfile | null
  seller: OfferProfile | null
  listing: OfferListing | null
}

export interface OfferRealtimeInitial {
  amount: number
  status: OfferStatus
  expiresAt: string
  paymentDeadline: string | null
  eventVersion: number
}

export interface OfferSlot {
  amount: number
  status: OfferStatus
  expiresAt: string
  paymentDeadline: string | null
  eventVersion: number
  declineReason: OfferDeclineReason | null
  parentOfferId: string | null
  settledAt: string | null
}

export interface ListingOffersAggregate {
  listingId: string
  pendingCount: number
  highestPending: number | null
}

export type PlaceOfferRpcResult =
  | {
      success: true
      offer_id: string
      status: OfferStatus
      auto_declined: boolean
      expires_at: string
      deduplicated?: boolean
    }
  | {
      success: false
      reason: PlaceOfferReason
      existing_offer_id?: string
      [k: string]: unknown
    }

export type PlaceOfferReason =
  | "unauthorized"
  | "invalid_amount"
  | "invalid_expires_in_hours"
  | "message_too_long"
  | "listing_not_found"
  | "listing_deleted"
  | "cannot_offer_own_listing"
  | "listing_does_not_accept_offers"
  | "buyer_banned"
  | "seller_banned"
  | "already_pending_offer"

export type AcceptOfferRpcResult =
  | { success: true; offer_id: string; payment_deadline: string }
  | { success: false; reason: AcceptOfferReason; state?: OfferStatus }

export type AcceptOfferReason =
  | "unauthorized"
  | "offer_not_found"
  | "forbidden"
  | "invalid_state"
  | "offer_expired"
  | "listing_unavailable"
  | "seller_banned"

export type DeclineOfferRpcResult =
  | { success: true; offer_id: string }
  | { success: false; reason: AcceptOfferReason; state?: OfferStatus }

export type CounterOfferRpcResult =
  | { success: true; offer_id: string; parent_offer_id: string; expires_at: string; deduplicated?: boolean }
  | { success: false; reason: CounterOfferReason; state?: OfferStatus }

export type CounterOfferReason =
  | "unauthorized"
  | "invalid_amount"
  | "invalid_expires_in_hours"
  | "message_too_long"
  | "parent_not_found"
  | "forbidden"
  | "invalid_parent_state"
  | "parent_expired"
  | "max_counter_depth_reached"
  | "listing_unavailable"
  | "listing_does_not_accept_offers"

export type WithdrawOfferRpcResult =
  | { success: true; offer_id: string }
  | { success: false; reason: AcceptOfferReason; state?: OfferStatus }

export const OFFER_EXPIRY_PRESETS_HOURS = [1, 6, 12, 24, 48, 72, 168] as const
export type OfferExpiryPresetHours = typeof OFFER_EXPIRY_PRESETS_HOURS[number]
export const DEFAULT_OFFER_EXPIRY_HOURS: OfferExpiryPresetHours = 48
export const MAX_OFFER_MESSAGE_LENGTH = 500
export const MAX_COUNTER_DEPTH = 5
